import abbLogo from '../assets/images/brands/abb/logo.png';
import sgsLogo from '../assets/images/logo.png';
import {
  BRAND_PREVIEW_STORAGE_KEY,
  brandIdFromSearch,
  isLocalHostname,
  resolveBrandId,
  type BrandId,
} from './resolveBrandId';

export interface Brand {
  id: BrandId;
  name: string;
  documentTitle: string;
  logo: string;
  logoAlt: string;
  favicon: string;
  appleTouchIcon: string;
  manifest: string;
}

const publicUrl = process.env.PUBLIC_URL || '';

export const BRANDS: Record<BrandId, Brand> = {
  sgs: {
    id: 'sgs',
    name: 'Smart Grid Solutions',
    documentTitle: 'SmartGrid Dashboard',
    logo: sgsLogo,
    logoAlt: 'Smart Grid Solutions',
    favicon: `${publicUrl}/favicon.ico`,
    appleTouchIcon: `${publicUrl}/logo192.png`,
    manifest: `${publicUrl}/manifest.json`,
  },
  abb: {
    id: 'abb',
    name: 'ABB',
    documentTitle: 'ABB Dashboard',
    logo: abbLogo,
    logoAlt: 'ABB',
    favicon: `${publicUrl}/brands/abb/favicon.ico`,
    appleTouchIcon: `${publicUrl}/brands/abb/logo192.png`,
    manifest: `${publicUrl}/manifest-abb.json`,
  },
};

function readStoredBrand(): string | null {
  try {
    return sessionStorage.getItem(BRAND_PREVIEW_STORAGE_KEY);
  } catch {
    return null;
  }
}

/** Persist a localhost `?brand=` choice so later routes keep the same preview. */
function rememberLocalBrandPreview(): void {
  if (typeof window === 'undefined' || !isLocalHostname(window.location.hostname)) {
    return;
  }

  const fromSearch = brandIdFromSearch(window.location.search);
  if (!fromSearch) {
    return;
  }

  try {
    sessionStorage.setItem(BRAND_PREVIEW_STORAGE_KEY, fromSearch);
  } catch {
    // Session storage can be unavailable in private browsing.
  }
}

export function getBrand(): Brand {
  if (typeof window === 'undefined') {
    return BRANDS.sgs;
  }

  const { hostname, search } = window.location;
  // Deployed hosts ignore the preview flag. Avoid sessionStorage on their render path.
  const stored = isLocalHostname(hostname) && !brandIdFromSearch(search) ? readStoredBrand() : null;
  return BRANDS[resolveBrandId(hostname, search, stored)];
}

function upsertLink(rel: string, href: string) {
  let link = document.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!link) {
    link = document.createElement('link');
    link.rel = rel;
    document.head.appendChild(link);
  }
  const next = new URL(href, window.location.origin).href;
  if (link.href !== next) {
    link.href = href;
  }
}

/** Tab title, favicon, and web-app manifest for the host the page was opened on. */
export function applyBrandHead() {
  rememberLocalBrandPreview();
  const brand = getBrand();
  document.title = brand.documentTitle;
  upsertLink('icon', brand.favicon);
  upsertLink('apple-touch-icon', brand.appleTouchIcon);
  upsertLink('manifest', brand.manifest);
}
